import { useLazyQuery } from '@apollo/client';
import { useNotification } from './useNotification';
import { GetUserByEmailQuery } from '../../insights/queries/UserQueries';

export interface OwnershipChangeParams {
  appName: string;
  appId: string;
  newOwnerLogin?: string;
  originalOwnerEmail?: string;
  currentUserEntityRef?: string;
}

/**
 * Hook to handle ownership change notifications for applications
 * Sends notifications to both new and original owners when ownership changes
 */
export const useOwnershipChangeNotifications = () => {
  const { sendNotification } = useNotification();
  const [getUserByEmail] = useLazyQuery(GetUserByEmailQuery);

  /**
   * Get the login for a user by their email address by querying Fusion
   * @param email - The email address of the user to look up
   * @returns The user's login or null if not found
   */
  const getLoginByEmail = async (email: string): Promise<string | null> => {
    try {
      const emailPrefix = email.split('@')[0];

      const { data } = await getUserByEmail({
        variables: {
          filter: {
            name: emailPrefix,
          },
        },
      });

      const users = data?.identity?.users || [];
      const matchingUser = users.find(
        (u: any) => u.email?.toLowerCase() === email.toLowerCase(),
      );

      return matchingUser?.login || null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to query Fusion for user login:', error);
      return null;
    }
  };

  /**
   * Send notifications when application ownership changes
   * Notifies both the new owner and the original owner
   * @param params - The ownership change parameters
   * @param params.appName - The name of the application
   * @param params.appId - The ID of the application
   * @param params.newOwnerLogin - The login of the new owner
   * @param params.originalOwnerEmail - The email of the original owner
   * @param params.currentUserEntityRef - The entity ref of the user making the change
   * @returns Promise that resolves when notifications are sent
   */
  const notifyOwnershipChange = async ({
    appName,
    appId,
    newOwnerLogin,
    originalOwnerEmail,
    currentUserEntityRef,
  }: OwnershipChangeParams): Promise<void> => {
    const currentUsername = currentUserEntityRef?.split('/').pop() || 'A user';

    // Get the original owner's login if we have their email
    let originalOwnerLogin: string | null = null;
    if (originalOwnerEmail) {
      originalOwnerLogin = await getLoginByEmail(originalOwnerEmail);
    }

    // Notify new owner if owner was assigned
    if (newOwnerLogin && newOwnerLogin !== 'clear') {
      const newOwnerEntityRef = `user:default/${newOwnerLogin}`;

      try {
        await sendNotification({
          recipient: newOwnerEntityRef,
          title: `You are now the owner of ${appName}`,
          description: `${currentUsername} assigned you as the owner for application "${appName}" (ID: ${appId}).`,
          link: `/catalog/default/system/${appId}`,
          severity: 'normal',
          topic: 'application-ownership-change',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send notification to new owner:', error);
      }

      // Notify original owner if they exist and are different from the new owner
      if (originalOwnerLogin && originalOwnerLogin !== newOwnerLogin) {
        const originalOwnerEntityRef = `user:default/${originalOwnerLogin}`;

        try {
          await sendNotification({
            recipient: originalOwnerEntityRef,
            title: `You are no longer the owner of ${appName}`,
            description: `${currentUsername} changed the owner for application "${appName}" (ID: ${appId}). The new owner is ${newOwnerLogin}.`,
            link: `/catalog/default/system/${appId}`,
            severity: 'normal',
            topic: 'application-ownership-change',
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to send notification to original owner:',
            error,
          );
        }
      }
    } else if (newOwnerLogin === 'clear' && originalOwnerLogin) {
      // Notify original owner when owner is removed
      const originalOwnerEntityRef = `user:default/${originalOwnerLogin}`;

      try {
        await sendNotification({
          recipient: originalOwnerEntityRef,
          title: `You are no longer the owner of ${appName}`,
          description: `${currentUsername} removed you as the owner for application "${appName}" (ID: ${appId}).`,
          link: `/catalog/default/system/${appId}`,
          severity: 'normal',
          topic: 'application-ownership-change',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send notification to removed owner:', error);
      }
    }
  };

  return {
    notifyOwnershipChange,
    getLoginByEmail,
  };
};
